import { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  FileButton,
  ActionIcon,
  Badge,
  Table,
  Modal,
  TextInput,
  Tooltip,
  Code,
  Box,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useDisclosure } from '@mantine/hooks';
import {
  IconUpload,
  IconTrash,
  IconFolder,
  IconFolderPlus,
  IconFolderOpen,
  IconEdit,
  IconPencil,
  IconCopy,
  IconCheck,
  IconRefresh,
  IconDownload,
  IconBook,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  useGetApiBotProviderAssetBundleListBundles,
  usePostApiBotProviderAssetBundleUploadZip,
  usePostApiBotProviderAssetBundleCreateEmptyBundle,
  usePostApiBotProviderAssetBundleRenameBundle,
  usePostApiBotProviderAssetBundleDuplicateBundle,
  useDeleteApiBotProviderAssetBundleDeleteBundle,
  AssetBundleItem,
  ExtensionSlotConfig,
} from '@/API';
import { GetBotProviderAssetBundleDownloadZipUrl } from '@/APIURL';
import { ExtensionManifestModal } from './ExtensionManifestModal';
import { AssetBundleExplorerModal } from './AssetBundleExplorerModal';
import { ExtensionDevGuideModal } from './ExtensionDevGuideModal';

interface AssetBundleManagerProps {
  workspaceId: string;
  botProviderId: string;
  extensions?: ExtensionSlotConfig[];
  onBundleRenamed?: (oldName: string, newName: string) => void;
}

export function AssetBundleManager({
  workspaceId,
  botProviderId,
  extensions = [],
  onBundleRenamed,
}: AssetBundleManagerProps) {
  const [uploadZipModalOpened, { open: openUploadZipModal, close: closeUploadZipModal }] = useDisclosure(false);
  const [createEmptyModalOpened, { open: openCreateEmptyModal, close: closeCreateEmptyModal }] = useDisclosure(false);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] = useDisclosure(false);
  const [duplicateModalOpened, { open: openDuplicateModal, close: closeDuplicateModal }] = useDisclosure(false);
  const [manifestModalOpened, { open: openManifestModal, close: closeManifestModal }] = useDisclosure(false);
  const [explorerModalOpened, { open: openExplorerModal, close: closeExplorerModal }] = useDisclosure(false);
  const [guideModalOpened, { open: openGuideModal, close: closeGuideModal }] = useDisclosure(false);

  const [selectedBundle, setSelectedBundle] = useState<AssetBundleItem | null>(null);
  const [bundleNameInput, setBundleNameInput] = useState('');
  const [selectedZipFile, setSelectedZipFile] = useState<File | null>(null);

  const [targetBundleForRename, setTargetBundleForRename] = useState('');
  const [renameInput, setRenameInput] = useState('');

  const [targetBundleForDuplicate, setTargetBundleForDuplicate] = useState('');
  const [duplicateInput, setDuplicateInput] = useState('');

  const bundlesQuery = useGetApiBotProviderAssetBundleListBundles({
    workspaceId,
    botProviderId,
  });

  const uploadZipMutation = usePostApiBotProviderAssetBundleUploadZip();
  const createEmptyBundleMutation = usePostApiBotProviderAssetBundleCreateEmptyBundle();
  const renameBundleMutation = usePostApiBotProviderAssetBundleRenameBundle();
  const duplicateBundleMutation = usePostApiBotProviderAssetBundleDuplicateBundle();
  const deleteBundleMutation = useDeleteApiBotProviderAssetBundleDeleteBundle();

  const isInvalidBundleName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    return /[\\/:*?"<>|\x00-\x1f]/.test(trimmed) || trimmed.includes('..');
  };

  const handleZipFilePicked = (file: File | null) => {
    if (!file) return;
    setSelectedZipFile(file);
    if (!bundleNameInput) {
      const baseName = file.name.replace(/\.[^/.]+$/, '').trim().replace(/[\\/:*?"<>|\x00-\x1f]/g, '_');
      setBundleNameInput(baseName);
    }
    openUploadZipModal();
  };

  const handleUploadZipSubmit = async () => {
    if (!selectedZipFile) return;

    try {
      await uploadZipMutation.mutateAsync({
        params: {
          workspaceId,
          botProviderId,
          bundleName: bundleNameInput || undefined,
        },
        data: {
          file: selectedZipFile,
        },
      });

      notifications.show({
        title: '上傳成功',
        message: `資產包已成功解壓並儲存`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      closeUploadZipModal();
      setSelectedZipFile(null);
      setBundleNameInput('');
      bundlesQuery.refetch();
    } catch (err: any) {
      notifications.show({
        title: '上傳失敗',
        message: err?.message || '上傳解壓過程發生錯誤，請確認檔案格式',
        color: 'red',
      });
    }
  };

  const handleCreateEmptySubmit = async () => {
    if (!bundleNameInput.trim()) return;

    try {
      const res = await createEmptyBundleMutation.mutateAsync({
        params: {
          workspaceId,
          botProviderId,
        },
        data: {
          bundleName: bundleNameInput.trim(),
        },
      });

      notifications.show({
        title: '建立成功',
        message: `已建立空白資產包 ${bundleNameInput}`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      closeCreateEmptyModal();
      setBundleNameInput('');
      await bundlesQuery.refetch();

      // Open explorer for newly created bundle
      if (res?.data) {
        setSelectedBundle(res.data);
        openExplorerModal();
      }
    } catch (err: any) {
      notifications.show({
        title: '建立失敗',
        message: err?.message || '建立資產包失敗，請確認名稱是否重複',
        color: 'red',
      });
    }
  };

  const handleDeleteBundle = (bundleName: string) => {
    const usedBySlots = extensions.filter(
      (ext) => ext.bundlePath === bundleName || ext.bundlePath?.startsWith(`${bundleName}/`)
    );

    modals.openConfirmModal({
      title: '確認刪除資產包',
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            確定要刪除資產包 <Code>{bundleName}</Code> 及其所有檔案嗎？此操作無法復原。
          </Text>
          {usedBySlots.length > 0 && (
            <Alert color="orange" title="關聯警告" icon={<IconAlertTriangle size={16} />}>
              此資產包正被以下擴充區引用：
              <Text size="xs" fw={600} mt={4}>
                {usedBySlots.map((s) => s.name || s.id).join('、')}
              </Text>
              刪除後擴充區將保留設定，但會標記為「資料不存在」。
            </Alert>
          )}
        </Stack>
      ),
      labels: { confirm: '確認刪除', cancel: '取消' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteBundleMutation.mutateAsync({
            params: {
              workspaceId,
              botProviderId,
              bundleName,
            },
          });

          notifications.show({
            title: '刪除成功',
            message: `已刪除資產包 ${bundleName}`,
            color: 'teal',
          });

          bundlesQuery.refetch();
        } catch (err: any) {
          notifications.show({
            title: '刪除失敗',
            message: err?.message || '刪除失敗，請稍後再試',
            color: 'red',
          });
        }
      },
    });
  };

  const handleOpenRenameBundle = (bundleName: string) => {
    setTargetBundleForRename(bundleName);
    setRenameInput(bundleName);
    openRenameModal();
  };

  const handleRenameBundleSubmit = async () => {
    if (!targetBundleForRename || !renameInput.trim()) return;

    try {
      await renameBundleMutation.mutateAsync({
        params: {
          workspaceId,
          botProviderId,
        },
        data: {
          oldBundleName: targetBundleForRename,
          newBundleName: renameInput.trim(),
        },
      });

      onBundleRenamed?.(targetBundleForRename, renameInput.trim());

      notifications.show({
        title: '重新命名成功',
        message: `已將資產包重新命名為 ${renameInput.trim()}`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      closeRenameModal();
      setTargetBundleForRename('');
      setRenameInput('');
      bundlesQuery.refetch();
    } catch (err: any) {
      notifications.show({
        title: '重新命名失敗',
        message: err?.message || '重新命名失敗，請確認名稱是否重複',
        color: 'red',
      });
    }
  };

  const handleOpenDuplicateBundle = (bundleName: string) => {
    setTargetBundleForDuplicate(bundleName);
    let candidate = `${bundleName}_copy`;
    let counter = 2;
    const existingNames = bundles.map((b) => b.bundleName?.toLowerCase());
    while (existingNames.includes(candidate.toLowerCase())) {
      candidate = `${bundleName}_copy_${counter}`;
      counter++;
    }
    setDuplicateInput(candidate);
    openDuplicateModal();
  };

  const handleDuplicateBundleSubmit = async () => {
    if (!targetBundleForDuplicate || !duplicateInput.trim()) return;

    try {
      await duplicateBundleMutation.mutateAsync({
        params: {
          workspaceId,
          botProviderId,
        },
        data: {
          sourceBundleName: targetBundleForDuplicate,
          targetBundleName: duplicateInput.trim(),
        },
      });

      notifications.show({
        title: '複製成功',
        message: `已建立資產包副本 ${duplicateInput.trim()}`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      closeDuplicateModal();
      setTargetBundleForDuplicate('');
      setDuplicateInput('');
      bundlesQuery.refetch();
    } catch (err: any) {
      notifications.show({
        title: '複製失敗',
        message: err?.message || '複製資產包失敗，請確認名稱是否重複',
        color: 'red',
      });
    }
  };

  const handleOpenExplorer = (bundle: AssetBundleItem) => {
    setSelectedBundle(bundle);
    openExplorerModal();
  };

  const handleOpenManifestEditor = (bundle: AssetBundleItem) => {
    setSelectedBundle(bundle);
    openManifestModal();
  };

  const bundles = bundlesQuery.data?.data || [];

  // Update selectedBundle when query updates
  const currentBundleForExplorer = selectedBundle
    ? bundles.find((b) => b.bundleName === selectedBundle.bundleName) || selectedBundle
    : null;

  const formatBytes = (rawBytes?: number | string | null) => {
    const bytes = typeof rawBytes === 'string' ? parseFloat(rawBytes) : (rawBytes || 0);
    if (!bytes || isNaN(bytes) || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <div>
            <Text fw={600} size="md">
              資產包雲端檔案管理
            </Text>
            <Text size="xs" c="dimmed">
              管理 Spine 骨骼、圖集、圖片序列或自訂網頁靜態資源，支援單檔新增/覆蓋/刪除與 Zip 整包匯入匯出
            </Text>
          </div>
          <Group gap="xs">
            <ActionIcon variant="light" onClick={() => bundlesQuery.refetch()} loading={bundlesQuery.isFetching}>
              <IconRefresh size={16} />
            </ActionIcon>
            <Button
              variant="light"
              color="indigo"
              leftSection={<IconBook size={16} />}
              size="xs"
              onClick={openGuideModal}
            >
              開發指南與 API
            </Button>
            <Button
              variant="light"
              leftSection={<IconFolderPlus size={16} />}
              size="xs"
              onClick={() => {
                setBundleNameInput('');
                openCreateEmptyModal();
              }}
            >
              建立空白資產包
            </Button>
            <FileButton onChange={handleZipFilePicked} accept="application/zip,.zip">
              {(props) => (
                <Button {...props} leftSection={<IconUpload size={16} />} size="xs">
                  上傳 Zip 資產包
                </Button>
              )}
            </FileButton>
          </Group>
        </Group>

        {bundles.length === 0 ? (
          <Box py="xl" style={{ textAlign: 'center', background: 'var(--mantine-color-gray-0)', borderRadius: '8px' }}>
            <IconFolder size={36} color="gray" style={{ margin: '0 auto 8px' }} />
            <Text size="sm" c="dimmed">
              目前尚未有任何資產包。你可以點擊「建立空白資產包」自行上傳檔案，或直接「上傳 Zip 資產包」自動解壓。
            </Text>
          </Box>
        ) : (
          <Table verticalSpacing="sm" withTableBorder striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>資料夾名稱</Table.Th>
                <Table.Th>檔案數量</Table.Th>
                <Table.Th>總大小</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>操作</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {bundles.map((bundle) => {
                const hasManifest = bundle.files?.some(
                  (f) => f.toLowerCase() === 'extension.json'
                );
                return (
                  <Table.Tr key={bundle.bundleName}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconFolder size={18} color="var(--mantine-color-blue-6)" />
                        <Text fw={500} size="sm">
                          {bundle.bundleName}
                        </Text>
                        {hasManifest && (
                          <Badge color="green" size="xs" variant="light">
                            已設定 Manifest
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="outline"
                        size="sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleOpenExplorer(bundle)}
                      >
                        {bundle.files?.length ?? 0} 個檔案 (檔案管理)
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {formatBytes(bundle.totalSize ?? 0)}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Group gap="xs" justify="flex-end">
                        <Button
                          variant="light"
                          size="xs"
                          leftSection={<IconFolderOpen size={14} />}
                          onClick={() => handleOpenExplorer(bundle)}
                        >
                          資產總管
                        </Button>
                        <Tooltip label="編輯 Manifest (設定動畫模板與狀態對應)">
                          <Button
                            variant="light"
                            color="teal"
                            size="xs"
                            leftSection={<IconEdit size={14} />}
                            onClick={() => handleOpenManifestEditor(bundle)}
                          >
                            設定模板
                          </Button>
                        </Tooltip>
                        <Tooltip label="重新命名資產包">
                          <ActionIcon
                            variant="light"
                            color="orange"
                            size="md"
                            onClick={() => handleOpenRenameBundle(bundle.bundleName!)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="建立資產包副本">
                          <ActionIcon
                            variant="light"
                            color="cyan"
                            size="md"
                            onClick={() => handleOpenDuplicateBundle(bundle.bundleName!)}
                          >
                            <IconCopy size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="整包打包下載為 .zip">
                          <ActionIcon
                            component="a"
                            href={GetBotProviderAssetBundleDownloadZipUrl(
                              workspaceId,
                              botProviderId,
                              bundle.bundleName!
                            )}
                            download={`${bundle.bundleName}.zip`}
                            variant="light"
                            color="blue"
                            size="md"
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          size="md"
                          onClick={() => handleDeleteBundle(bundle.bundleName!)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* Create Empty Bundle Modal */}
      <Modal opened={createEmptyModalOpened} onClose={closeCreateEmptyModal} title="建立空白資產包" centered>
        <Stack gap="md">
          <TextInput
            label="資產包名稱 (資料夾名稱)"
            placeholder="例如: mascot_v2 或 客服套件"
            value={bundleNameInput}
            onChange={(e) => setBundleNameInput(e.currentTarget.value)}
            description="支援中英文、數字、底線或破折號，建立後可直接透過資產總管上傳或覆蓋個別檔案"
            error={isInvalidBundleName(bundleNameInput) ? '名稱不能包含 / \\ : * ? " < > | 或路徑穿越符號 (..)' : undefined}
            withAsterisk
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeCreateEmptyModal}>
              取消
            </Button>
            <Button
              onClick={handleCreateEmptySubmit}
              loading={createEmptyBundleMutation.isPending}
              disabled={!bundleNameInput.trim() || isInvalidBundleName(bundleNameInput)}
            >
              建立資產包
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Upload Zip Modal */}
      <Modal opened={uploadZipModalOpened} onClose={closeUploadZipModal} title="確認上傳 Zip 資產包" centered>
        <Stack gap="md">
          <Text size="sm">
            已選取檔案: <Code>{selectedZipFile?.name}</Code> ({formatBytes(selectedZipFile?.size ?? 0)})
          </Text>
          <TextInput
            label="資產包名稱 (資料夾名稱)"
            placeholder="例如: mascot-alice 或 客服套件"
            value={bundleNameInput}
            onChange={(e) => setBundleNameInput(e.currentTarget.value)}
            description="支援中英文、數字、底線或破折號，將作為該資源的識別路徑"
            error={isInvalidBundleName(bundleNameInput) ? '名稱不能包含 / \\ : * ? " < > | 或路徑穿越符號 (..)' : undefined}
            withAsterisk
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeUploadZipModal}>
              取消
            </Button>
            <Button
              onClick={handleUploadZipSubmit}
              loading={uploadZipMutation.isPending}
              disabled={!bundleNameInput.trim() || isInvalidBundleName(bundleNameInput)}
            >
              開始解壓與上傳
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Rename Bundle Modal */}
      <Modal opened={renameModalOpened} onClose={closeRenameModal} title="重新命名資產包" centered>
        <Stack gap="md">
          <Text size="sm">
            原資產包名稱: <Code>{targetBundleForRename}</Code>
          </Text>
          <TextInput
            label="新資產包名稱"
            placeholder="例如: mascot_v3"
            value={renameInput}
            onChange={(e) => setRenameInput(e.currentTarget.value)}
            description="重新命名將安全移動該資產包在雲端儲存庫中的目錄，並自動同步關聯的擴充槽位設定"
            error={
              isInvalidBundleName(renameInput)
                ? '名稱不能包含 / \\ : * ? " < > | 或路徑穿越符號 (..)'
                : bundles.some(
                    (b) =>
                      b.bundleName?.toLowerCase() === renameInput.trim().toLowerCase() &&
                      b.bundleName?.toLowerCase() !== targetBundleForRename.toLowerCase()
                  )
                ? '此資產包名稱已存在'
                : undefined
            }
            withAsterisk
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeRenameModal}>
              取消
            </Button>
            <Button
              onClick={handleRenameBundleSubmit}
              loading={renameBundleMutation.isPending}
              disabled={
                !renameInput.trim() ||
                isInvalidBundleName(renameInput) ||
                renameInput.trim().toLowerCase() === targetBundleForRename.toLowerCase() ||
                bundles.some(
                  (b) =>
                    b.bundleName?.toLowerCase() === renameInput.trim().toLowerCase() &&
                    b.bundleName?.toLowerCase() !== targetBundleForRename.toLowerCase()
                )
              }
            >
              確認重新命名
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Duplicate Bundle Modal */}
      <Modal opened={duplicateModalOpened} onClose={closeDuplicateModal} title="建立資產包副本" centered>
        <Stack gap="md">
          <Text size="sm">
            來源資產包: <Code>{targetBundleForDuplicate}</Code>
          </Text>
          <TextInput
            label="新副本資產包名稱"
            placeholder="例如: mascot_copy"
            value={duplicateInput}
            onChange={(e) => setDuplicateInput(e.currentTarget.value)}
            description="將完整複製來源資產包的所有檔案至新名稱資產包"
            error={
              isInvalidBundleName(duplicateInput)
                ? '名稱不能包含 / \\ : * ? " < > | 或路徑穿越符號 (..)'
                : bundles.some((b) => b.bundleName?.toLowerCase() === duplicateInput.trim().toLowerCase())
                ? '此資產包名稱已存在'
                : undefined
            }
            withAsterisk
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeDuplicateModal}>
              取消
            </Button>
            <Button
              onClick={handleDuplicateBundleSubmit}
              loading={duplicateBundleMutation.isPending}
              disabled={
                !duplicateInput.trim() ||
                isInvalidBundleName(duplicateInput) ||
                bundles.some((b) => b.bundleName?.toLowerCase() === duplicateInput.trim().toLowerCase())
              }
            >
              建立副本
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Full Explorer Modal */}
      {currentBundleForExplorer && (
        <AssetBundleExplorerModal
          key={currentBundleForExplorer.bundleName}
          opened={explorerModalOpened}
          onClose={() => {
            closeExplorerModal();
            bundlesQuery.refetch();
          }}
          workspaceId={workspaceId}
          botProviderId={botProviderId}
          bundle={currentBundleForExplorer}
          onBundleUpdated={(updated) => {
            if (updated) {
              setSelectedBundle(updated);
            }
            bundlesQuery.refetch();
          }}
          onOpenManifestEditor={() => {
            closeExplorerModal();
            handleOpenManifestEditor(currentBundleForExplorer);
          }}
        />
      )}

      {/* Manifest Editor Modal */}
      {selectedBundle && (
        <ExtensionManifestModal
          key={selectedBundle.bundleName}
          opened={manifestModalOpened}
          onClose={() => {
            closeManifestModal();
            bundlesQuery.refetch();
          }}
          workspaceId={workspaceId}
          botProviderId={botProviderId}
          bundle={selectedBundle}
          onBundleUpdated={(updated) => {
            if (updated) setSelectedBundle(updated);
            bundlesQuery.refetch();
          }}
        />
      )}

      {/* Developer Guide & API Reference Modal */}
      <ExtensionDevGuideModal
        opened={guideModalOpened}
        onClose={closeGuideModal}
        defaultTab="iframe"
      />
    </Paper>
  );
}
